/**
 * Welcome kit vs protein bag cart split.
 * First protein unit is the welcome kit (+ gift whisk). Extra units are bag SKUs.
 * Cart UI can group those lines into one quantity.
 */
(function (global) {
  const CONFIG_ID = 'ForallProteinSplitConfig';
  let cachedConfig = null;

  function parseConfig() {
    if (cachedConfig) return cachedConfig;

    const el = document.getElementById(CONFIG_ID);
    if (!el || !el.textContent) {
      cachedConfig = { enabled: false, mode: 'off' };
      return cachedConfig;
    }

    try {
      cachedConfig = JSON.parse(el.textContent);
    } catch (err) {
      cachedConfig = { enabled: false, mode: 'off' };
    }

    return cachedConfig;
  }

  function isEnabled() {
    const cfg = parseConfig();
    return Boolean(cfg && cfg.enabled && cfg.kitProductId && cfg.bagVariantId);
  }

  function getMode() {
    const cfg = parseConfig();
    return cfg.mode || 'off';
  }

  function isGiftCartItem(item) {
    return Boolean(item && item.properties && item.properties._gift_item);
  }

  function isKitItem(item) {
    const cfg = parseConfig();
    if (!item || isGiftCartItem(item) || !cfg.kitProductId) return false;
    if (cfg.bagVariantId && String(item.variant_id) === String(cfg.bagVariantId)) return false;
    return String(item.product_id) === String(cfg.kitProductId);
  }

  function isBagItem(item) {
    const cfg = parseConfig();
    if (!item || isGiftCartItem(item)) return false;
    if (cfg.bagVariantId && String(item.variant_id) === String(cfg.bagVariantId)) return true;
    if (!cfg.bagProductId) return false;
    if (cfg.kitProductId && String(cfg.bagProductId) === String(cfg.kitProductId)) return false;
    return String(item.product_id) === String(cfg.bagProductId);
  }

  function cartHasKit(cart) {
    if (!cart || !cart.items) return false;
    for (let i = 0; i < cart.items.length; i++) {
      if (isKitItem(cart.items[i])) return true;
    }
    return false;
  }

  function cartHasGift(cart) {
    if (!cart || !cart.items) return false;
    for (let i = 0; i < cart.items.length; i++) {
      if (isGiftCartItem(cart.items[i])) return true;
    }
    return false;
  }

  function sumQty(items) {
    let total = 0;
    for (let i = 0; i < items.length; i++) {
      total += items[i].quantity;
    }
    return total;
  }

  function collectLines(cart, matcher) {
    const lines = [];
    if (!cart || !cart.items) return lines;
    for (let i = 0; i < cart.items.length; i++) {
      if (matcher(cart.items[i])) lines.push(cart.items[i]);
    }
    return lines;
  }

  function getKitLines(cart) {
    return collectLines(cart, isKitItem);
  }

  function getBagLines(cart) {
    return collectLines(cart, isBagItem);
  }

  function isSubscriptionLine(item) {
    return Boolean(
      item &&
        item.selling_plan_allocation &&
        item.selling_plan_allocation.selling_plan &&
        item.selling_plan_allocation.selling_plan.id
    );
  }

  function getSubKitLines(cart) {
    return collectLines(cart, function (item) {
      return isKitItem(item) && isSubscriptionLine(item);
    });
  }

  function getSubBagLines(cart) {
    return collectLines(cart, function (item) {
      return isBagItem(item) && isSubscriptionLine(item);
    });
  }

  function getOtpKitLines(cart) {
    return collectLines(cart, function (item) {
      return isKitItem(item) && !isSubscriptionLine(item);
    });
  }

  function getOtpBagLines(cart) {
    return collectLines(cart, function (item) {
      return isBagItem(item) && !isSubscriptionLine(item);
    });
  }

  function getGiftLines(cart) {
    return collectLines(cart, isGiftCartItem);
  }

  function proteinQty(cart) {
    return sumQty(getKitLines(cart)) + sumQty(getBagLines(cart));
  }

  function lineSellingPlanId(item) {
    if (
      item &&
      item.selling_plan_allocation &&
      item.selling_plan_allocation.selling_plan &&
      item.selling_plan_allocation.selling_plan.id
    ) {
      return item.selling_plan_allocation.selling_plan.id;
    }
    return '';
  }

  function planKeyFromId(planId) {
    if (!planId) return '';

    const cfg = parseConfig();
    const lists = [cfg.kitPlans || [], cfg.bagPlans || []];

    for (let i = 0; i < lists.length; i++) {
      for (let j = 0; j < lists[i].length; j++) {
        if (String(lists[i][j].id) === String(planId)) {
          return lists[i][j].key || '';
        }
      }
    }

    return '';
  }

  function bagPlanForKey(frequencyKey) {
    const cfg = parseConfig();
    const key = String(frequencyKey || '');
    if (!key) return null;

    if (cfg.bagPlanByKey && cfg.bagPlanByKey[key]) {
      return parseInt(cfg.bagPlanByKey[key], 10);
    }

    const bagPlans = cfg.bagPlans || [];
    for (let i = 0; i < bagPlans.length; i++) {
      if (bagPlans[i].key === key) {
        return parseInt(bagPlans[i].id, 10);
      }
    }

    return null;
  }

  function isBagPlanId(planId) {
    if (!planId) return false;
    const cfg = parseConfig();
    const bagPlans = cfg.bagPlans || [];
    for (let i = 0; i < bagPlans.length; i++) {
      if (String(bagPlans[i].id) === String(planId)) return true;
    }
    return false;
  }

  function matchBagSellingPlan(kitPlanId, frequencyKey) {
    const cfg = parseConfig();

    if (kitPlanId && isBagPlanId(kitPlanId)) {
      return parseInt(kitPlanId, 10);
    }

    if (frequencyKey) {
      const fromKey = bagPlanForKey(frequencyKey);
      if (fromKey) return fromKey;
    }

    if (!kitPlanId) return null;

    if (cfg.planMap && cfg.planMap[String(kitPlanId)]) {
      const mapped = parseInt(cfg.planMap[String(kitPlanId)], 10);
      if (mapped) return mapped;
    }

    const fromKitKey = bagPlanForKey(planKeyFromId(kitPlanId));
    if (fromKitKey) return fromKitKey;

    return parseInt(kitPlanId, 10);
  }

  function parseBagCount(title) {
    if (!title) return 1;
    const match = String(title).match(/(\d+)\s*(pack|bag)/i);
    if (match) return Math.max(1, parseInt(match[1], 10));
    const leading = String(title).match(/^(\d+)/);
    if (leading) return Math.max(1, parseInt(leading[1], 10));
    return 1;
  }

  function buildSplitItems(options) {
    const opts = options || {};
    const cfg = parseConfig();
    const quantity = Math.max(1, parseInt(opts.quantity, 10) || 1);
    const mode = opts.mode || getMode();
    const cart = opts.cart || { items: [] };
    const includeGift = opts.includeGift === true;
    const giftVariantId = opts.giftVariantId || cfg.giftVariantId;
    const giftParentId = opts.giftParentId || cfg.kitProductId;
    const kitVariantId = opts.kitVariantId || cfg.kitSingleVariantId || cfg.kitVariantId;
    const bagVariantId = opts.bagVariantId || cfg.bagVariantId;
    const sellingPlanId = opts.sellingPlanId ? parseInt(opts.sellingPlanId, 10) : null;
    let bagSellingPlanId = null;

    if (opts.bagSellingPlanId) {
      bagSellingPlanId = parseInt(opts.bagSellingPlanId, 10);
    } else {
      bagSellingPlanId = matchBagSellingPlan(sellingPlanId, opts.frequencyKey);
    }

    if (!bagSellingPlanId && sellingPlanId) {
      bagSellingPlanId = sellingPlanId;
    }

    const items = [];

    if (mode === 'bag') {
      if (!bagVariantId) return null;
      const bagItem = { id: bagVariantId, quantity: quantity };
      if (bagSellingPlanId) {
        bagItem.selling_plan = bagSellingPlanId;
      }
      items.push(bagItem);
      return items;
    }

    if (mode !== 'kit' || !kitVariantId || !bagVariantId) {
      return null;
    }

    const hasKit = cartHasKit(cart);
    let kitToAdd = 0;
    let bagsToAdd = 0;

    if (!hasKit) {
      kitToAdd = 1;
      bagsToAdd = quantity - 1;
    } else {
      bagsToAdd = quantity;
    }

    if (includeGift && !cartHasGift(cart) && giftVariantId && (kitToAdd > 0 || hasKit)) {
      items.push({
        id: giftVariantId,
        quantity: 1,
        properties: { _gift_item: String(giftParentId) },
      });
    }

    if (kitToAdd > 0) {
      const kitItem = { id: kitVariantId, quantity: kitToAdd };
      if (sellingPlanId) kitItem.selling_plan = sellingPlanId;
      items.push(kitItem);
    }

    if (bagsToAdd > 0) {
      const bagItem = { id: bagVariantId, quantity: bagsToAdd };
      if (bagSellingPlanId) bagItem.selling_plan = bagSellingPlanId;
      items.push(bagItem);
    }

    return items;
  }

  function cartAjaxUrl(routeUrl, fallback) {
    const base = routeUrl || fallback;
    if (!base) return fallback;
    if (base.slice(-3) === '.js') return base;
    return base + '.js';
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error('Cart error ' + res.status);
      return res.json();
    });
  }

  function addItems(items, sections) {
    const body = { items: items };
    if (sections && sections.sections) {
      body.sections = sections.sections;
      body.sections_url = sections.sections_url;
    }
    return postJson(
      cartAjaxUrl(global.routes && global.routes.cart_add_url, '/cart/add.js'),
      body
    );
  }

  function updateCart(updates, sections) {
    const body = { updates: updates };
    if (sections && sections.sections) {
      body.sections = sections.sections;
      body.sections_url = sections.sections_url;
    }
    return postJson(
      cartAjaxUrl(global.routes && global.routes.cart_update_url, '/cart/update.js'),
      body
    );
  }

  function setGroupedQuantity(cart, targetQty, sections, groupType) {
    const cfg = parseConfig();
    const isOtp = groupType === 'otp';
    const target = Math.max(0, parseInt(targetQty, 10) || 0);
    const kitLines = isOtp ? getOtpKitLines(cart) : getSubKitLines(cart);
    const bagLines = isOtp ? getOtpBagLines(cart) : getSubBagLines(cart);
    const giftLines = getGiftLines(cart);
    const current = sumQty(kitLines) + sumQty(bagLines);

    if (target === current && target > 0) {
      return Promise.resolve(null);
    }

    if (target < 1) {
      const updates = {};
      const otherKits = isOtp ? getSubKitLines(cart) : getOtpKitLines(cart);
      for (let i = 0; i < kitLines.length; i++) updates[kitLines[i].key] = 0;
      for (let j = 0; j < bagLines.length; j++) updates[bagLines[j].key] = 0;
      if (otherKits.length === 0) {
        for (let g = 0; g < giftLines.length; g++) updates[giftLines[g].key] = 0;
      }
      if (Object.keys(updates).length === 0) return Promise.resolve(null);
      return updateCart(updates, sections);
    }

    const primaryKit = kitLines[0];
    const primaryBag = bagLines[0];
    const bagsNeeded = primaryKit ? target - 1 : target;
    let bagPlanId = null;
    if (!isOtp) {
      const sellingPlanId = lineSellingPlanId(primaryKit) || lineSellingPlanId(primaryBag);
      bagPlanId = matchBagSellingPlan(sellingPlanId) || (isBagPlanId(lineSellingPlanId(primaryBag))
        ? parseInt(lineSellingPlanId(primaryBag), 10)
        : null);
    }

    if (target > current) {
      const delta = target - current;

      if (kitLines.length === 0 && !isOtp) {
        const sellingPlanId = lineSellingPlanId(primaryBag);
        const items = [];
        const kitItem = { id: cfg.kitVariantId, quantity: 1 };
        if (sellingPlanId) kitItem.selling_plan = parseInt(sellingPlanId, 10);
        items.push(kitItem);
        if (target > 1) {
          const bagItem = { id: cfg.bagVariantId, quantity: target - 1 };
          if (bagPlanId) bagItem.selling_plan = parseInt(bagPlanId, 10);
          items.push(bagItem);
        }
        return addItems(items, sections);
      }

      const addBody = {
        id: cfg.bagVariantId,
        quantity: delta,
      };
      if (bagPlanId) {
        addBody.selling_plan = parseInt(bagPlanId, 10);
      }
      return addItems([addBody], sections);
    }

    const updates = {};
    if (primaryKit) {
      updates[primaryKit.key] = 1;
      for (let i = 1; i < kitLines.length; i++) updates[kitLines[i].key] = 0;
    }

    if (bagsNeeded < 1) {
      for (let j = 0; j < bagLines.length; j++) updates[bagLines[j].key] = 0;
      return updateCart(updates, sections);
    }

    if (primaryBag) {
      updates[primaryBag.key] = bagsNeeded;
      for (let j = 1; j < bagLines.length; j++) updates[bagLines[j].key] = 0;
      return updateCart(updates, sections);
    }

    return updateCart(updates, null).then(function () {
      const bagItem = { id: cfg.bagVariantId, quantity: bagsNeeded };
      if (bagPlanId) bagItem.selling_plan = parseInt(bagPlanId, 10);
      return addItems([bagItem], sections);
    });
  }

  function buildReconcileActions(cart) {
    const cfg = parseConfig();
    if (!isEnabled() || !cart || !cart.items) return null;

    const kitLines = getKitLines(cart);
    const kitQty = sumQty(kitLines);
    if (kitQty <= 1) return null;

    const primary = kitLines[0];
    const excess = kitQty - 1;
    const updates = {};
    updates[primary.key] = 1;
    for (let i = 1; i < kitLines.length; i++) {
      updates[kitLines[i].key] = 0;
    }

    const sellingPlanId = lineSellingPlanId(primary);
    const bagPlanId = matchBagSellingPlan(sellingPlanId);
    const addItem = { id: cfg.bagVariantId, quantity: excess };
    if (bagPlanId) addItem.selling_plan = bagPlanId;

    return { updates: updates, addItem: addItem };
  }

  function reconcileKitQty(sections) {
    if (!isEnabled()) return Promise.resolve(null);

    const cartUrl =
      (global.routes && global.routes.cart_url ? global.routes.cart_url : '/cart') + '.js';

    return fetch(cartUrl, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('Cart fetch error');
        return res.json();
      })
      .then(function (cart) {
        const actions = buildReconcileActions(cart);
        if (!actions) return null;
        return updateCart(actions.updates, null).then(function () {
          return addItems([actions.addItem], sections);
        });
      })
      .catch(function () {
        return null;
      });
  }

  global.ForallProteinSplit = {
    getConfig: parseConfig,
    isEnabled: isEnabled,
    getMode: getMode,
    isGiftCartItem: isGiftCartItem,
    isKitItem: isKitItem,
    isBagItem: isBagItem,
    cartHasKit: cartHasKit,
    cartHasGift: cartHasGift,
    getKitLines: getKitLines,
    getBagLines: getBagLines,
    proteinQty: proteinQty,
    planKeyFromId: planKeyFromId,
    bagPlanForKey: bagPlanForKey,
    matchBagSellingPlan: matchBagSellingPlan,
    parseBagCount: parseBagCount,
    buildSplitItems: buildSplitItems,
    addItems: addItems,
    setGroupedQuantity: setGroupedQuantity,
    reconcileKitQty: reconcileKitQty,
  };
})(window);
